import React, { useState, useEffect } from 'react';
import emailjs from 'emailjs-com';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const PRODUCT_TYPES = [
  { value: 'coretec', label: 'COREtec Floors' },
  { value: 'laminate', label: 'Shaw Laminate' },
  { value: 'vinyl', label: 'Shaw Vinyl' },
  { value: 'hardwood', label: 'Shaw Hardwood' },
  { value: 'porcelain', label: 'Shaw Porcelain' },
  { value: 'anderson-tuftex-hardwood', label: 'Anderson Tuftex Hardwood' },
  { value: 'multiple', label: 'Multiple Products' },
  { value: 'Other', label: 'Other' }
];

const EMPTY_SAMPLE = { style: '', color: '', qty: '1' };

const INITIAL_FORM = {
  shipTo: 'store',
  recipientName: '',
  deliveryPhone: '',
  street: '',
  unit: '',
  city: '',
  state: '',
  zip: '',
  name: '',
  email: '',
  phone: '',
  accountNumber: '',
  company: '',
  productType: '',
  additionalInfo: ''
};

const buildReference = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `SR-${String(d.getFullYear()).slice(2)}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
};

function Samples() {
  const [showSuccess, setShowSuccess] = useState(false);
  const [successRef, setSuccessRef] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [samples, setSamples] = useState([{ ...EMPTY_SAMPLE }]);
  const [formData, setFormData] = useState({ ...INITIAL_FORM });

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init("EsGqyOamAwbbhxg7e"); // Your EmailJS Public Key
  }, []);

  const toCustomer = formData.shipTo === 'customer';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSampleChange = (index, field, value) => {
    setSamples(prev => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addSample = () => {
    setSamples(prev => [...prev, { ...EMPTY_SAMPLE }]);
  };

  const removeSample = (index) => {
    setSamples(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const reference = buildReference();
    const productLabel = (PRODUCT_TYPES.find(p => p.value === formData.productType) || {}).label || formData.productType;
    const shipToLabel = toCustomer ? 'Customer' : 'My Store';

    const addressLines = [
      formData.street,
      formData.unit,
      `${formData.city}, ${formData.state} ${formData.zip}`
    ].filter(Boolean);

    const destinationBlock = toCustomer
      ? [
          'SHIP TO: CUSTOMER',
          `Attn: ${formData.recipientName}`,
          ...addressLines,
          `Delivery phone: ${formData.deliveryPhone}`
        ]
      : [
          'SHIP TO: MY STORE',
          formData.company,
          ...addressLines
        ];

    const sampleLines = samples
      .filter(row => row.style || row.color)
      .map(row => `- ${row.qty || '1'} x ${row.style}${row.color ? ` - ${row.color}` : ''}`);

    const messageBody = [
      destinationBlock.join('\n'),
      '',
      `Reference: ${reference}`,
      '',
      'Samples Requested:',
      sampleLines.join('\n'),
      '',
      `Product Type: ${productLabel}`,
      '',
      'Requested By:',
      `- Name: ${formData.name}`,
      `- Email: ${formData.email}`,
      `- Phone: ${formData.phone || 'Not provided'}`,
      `- Company: ${formData.company}`,
      `- Account Number: ${formData.accountNumber || 'Not provided'}`,
      '',
      'Additional Information:',
      formData.additionalInfo || 'None provided'
    ].join('\n');

    try {
      const templateParams = {
        to_name: 'Jason Ranney',
        to_email: 'jpkicker@gmail.com',
        from_name: formData.name,
        from_email: formData.email,
        reference: reference,
        company: formData.company,
        product_type: productLabel,
        ship_to: shipToLabel,
        message: messageBody
      };

      const response = await emailjs.send(
        'service_nfks9v2',
        'template_ba7capf',
        templateParams
      );

      if (response.status === 200) {
        setSuccessRef(reference);
        setShowSuccess(true);
        setFormData({ ...INITIAL_FORM });
        setSamples([{ ...EMPTY_SAMPLE }]);

        setTimeout(() => {
          setShowSuccess(false);
        }, 12000);
      }
    } catch (error) {
      console.error('EmailJS Error Details:', error);
      alert(`Failed to submit request. Error: ${error.message || 'Unknown error'}. Please try again or contact us directly at jpkicker@gmail.com`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const helpText = { display: 'block', marginTop: '0.4rem', fontSize: '0.85rem', color: '#64748b', fontWeight: '400' };
  const radioLabel = { display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '400', color: '#334155', marginBottom: 0 };

  return (
    <div id="samples" className="page">
      <section className="form-section">
        <div className="container">
          <h2 style={{ textAlign: 'center', fontSize: '2.5rem', marginBottom: '2rem', color: '#1e3a8a' }}>Order Your Flooring Samples</h2>
          <p style={{ textAlign: 'center', fontSize: '1.2rem', color: '#64748b', marginBottom: '3rem' }}>Get free samples of premium Shaw flooring products for customer presentations and sales support. Ship them to your store, or send them straight to your customer.</p>

          <div className="form-container">
            {showSuccess && (
              <div className="success-message">
                Thank you! Your sample request has been submitted.<br />
                Reference: <strong>{successRef}</strong><br />
                Samples typically ship within 3&ndash;5 business days.
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Where should these samples ship? *</label>
                <div style={{ display: 'flex', gap: '2rem', marginTop: '0.5rem' }}>
                  <label style={radioLabel}>
                    <input
                      type="radio"
                      name="shipTo"
                      value="store"
                      checked={formData.shipTo === 'store'}
                      onChange={handleInputChange}
                      style={{ width: 'auto', padding: 0 }}
                    />
                    My store
                  </label>
                  <label style={radioLabel}>
                    <input
                      type="radio"
                      name="shipTo"
                      value="customer"
                      checked={toCustomer}
                      onChange={handleInputChange}
                      style={{ width: 'auto', padding: 0 }}
                    />
                    My customer
                  </label>
                </div>
              </div>

              {toCustomer && (
                <div className="form-group">
                  <label htmlFor="recipientName">Recipient Name *</label>
                  <input
                    type="text"
                    id="recipientName"
                    name="recipientName"
                    value={formData.recipientName}
                    onChange={handleInputChange}
                    placeholder="Who should the box be addressed to?"
                    required
                  />
                </div>
              )}

              {toCustomer && (
                <div className="form-group">
                  <label htmlFor="deliveryPhone">Delivery Phone Number *</label>
                  <input
                    type="tel"
                    id="deliveryPhone"
                    name="deliveryPhone"
                    value={formData.deliveryPhone}
                    onChange={handleInputChange}
                    required
                  />
                  <span style={helpText}>Carriers require a phone number for residential deliveries.</span>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="street">{toCustomer ? 'Customer Delivery Address *' : 'Business Shipping Address *'}</label>
                <input
                  type="text"
                  id="street"
                  name="street"
                  value={formData.street}
                  onChange={handleInputChange}
                  placeholder="Street address"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="unit">Suite / Unit</label>
                <input
                  type="text"
                  id="unit"
                  name="unit"
                  value={formData.unit}
                  onChange={handleInputChange}
                  placeholder="Optional"
                />
              </div>

              <div className="form-group">
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label htmlFor="city">City *</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="state">State *</label>
                    <select
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">--</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="zip">ZIP Code *</label>
                    <input
                      type="text"
                      id="zip"
                      name="zip"
                      value={formData.zip}
                      onChange={handleInputChange}
                      placeholder="33414"
                      pattern="[0-9]{5}(-[0-9]{4})?"
                      title="Enter a 5-digit ZIP code"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="productType">Product Type of Interest *</label>
                <select
                  id="productType"
                  name="productType"
                  value={formData.productType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Product Type</option>
                  {PRODUCT_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Samples Requested *</label>
                <span style={{ ...helpText, marginTop: 0, marginBottom: '0.75rem' }}>One line per sample, for example: VV810 &nbsp;|&nbsp; Sea Salt Oak &nbsp;|&nbsp; 2</span>
                {samples.map((row, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: samples.length > 1 ? '1fr 1.6fr 0.6fr auto' : '1fr 1.6fr 0.6fr',
                      gap: '0.75rem',
                      marginBottom: '0.75rem'
                    }}
                  >
                    <input
                      type="text"
                      value={row.style}
                      onChange={(e) => handleSampleChange(index, 'style', e.target.value)}
                      placeholder="Style #"
                      aria-label={`Sample ${index + 1} style number`}
                      required
                    />
                    <input
                      type="text"
                      value={row.color}
                      onChange={(e) => handleSampleChange(index, 'color', e.target.value)}
                      placeholder="Style / color name"
                      aria-label={`Sample ${index + 1} color name`}
                    />
                    <input
                      type="number"
                      min="1"
                      value={row.qty}
                      onChange={(e) => handleSampleChange(index, 'qty', e.target.value)}
                      aria-label={`Sample ${index + 1} quantity`}
                      required
                    />
                    {samples.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSample(index)}
                        style={{ background: 'white', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0 1rem', color: '#64748b', cursor: 'pointer', fontSize: '0.9rem' }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addSample}
                  style={{ background: 'none', border: 'none', padding: 0, color: '#1e3a8a', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer' }}
                >
                  + Add another sample
                </button>
              </div>

              <div className="form-group">
                <label htmlFor="name">Your Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="company">Company/Store Name *</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="ABC Flooring Inc."
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="accountNumber">Account Number</label>
                <input
                  type="text"
                  id="accountNumber"
                  name="accountNumber"
                  value={formData.accountNumber}
                  onChange={handleInputChange}
                  placeholder="Your account number (if applicable)"
                />
                <span style={helpText}>Found on your invoice. Including it speeds up processing.</span>
              </div>

              <div className="form-group">
                <label htmlFor="additionalInfo">Additional Information or Special Requirements</label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  placeholder="Needed by a certain date? Anything else we should know?"
                  value={formData.additionalInfo}
                  onChange={handleInputChange}
                ></textarea>
              </div>

              <button
                type="submit"
                className="btn"
                style={{ width: '100%' }}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Request Samples'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Samples;
